import {
  AssetType,
  GasStorageDataType,
  GasStorageVisualization,
  OptimizationParameterValue,
} from '@e2m-customer-portal/domains';
import { useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { Nullable, mustBeGreaterThan, mustBeLessThan } from '@e2m-customer-portal/shared';
import {
  DateTimePicker,
  InfoTooltip,
  NumberInput,
  TimePicker,
  ValidatedContainer,
  WeekdaySelection,
  WeekdaySelector,
} from '@e2m-customer-portal/ui';
import { Button, CircularProgress, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import clsx from 'clsx';
import { getDay, getHours, getMinutes, parseISO, roundToNearestMinutes, toDate } from 'date-fns';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SoCInitializationMode, WeeklyFillLevelConfig } from './types';
interface GasStorageProps {
  data: Nullable<GasStorageDataType>;
  updateValues: (parameters: string) => void;
  isSaving?: boolean;
  readOnly: (parameter?: string) => boolean;
}

export function GasStorage({ data, updateValues, isSaving, readOnly }: GasStorageProps) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();
  const [isValid, setIsValid] = useState(false);
  const inputClass = 'w-full';

  const [updatedData, setUpdatedData] = useState({ ...data });
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());

  if (!data) return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE_GAS_STORAGE')}</div>;

  const updateGasStorageValues = (isValid: boolean) => {
    const updatedItems = Object.fromEntries(changes);
    if (isValid) {
      const mutationBody = JSON.stringify({
        asset_type: AssetType.Biogas,
        storage: updatedItems,
      });
      updateValues(mutationBody);
      setChanges(new Map());
    }
  };

  const resetValues = () => {
    setUpdatedData({ ...data });
    setChanges(new Map());
  };

  if (!data) return <div className="font-medium test-lg p-4">{t('COPT_MISSING_PARAMETERS_MESSAGE_GAS_STORAGE')}</div>;

  const getTime = (timeString: string) => timeString.split(':').map(Number);

  return (
    <div className="mx-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4" data-testid="gas-storage">
      <ValidatedContainer
        validate="on"
        onValidated={setIsValid}
        resetValidationTrigger={updatedData.soc_initialization_mode}
      >
        <div className="w-full flex flex-col gap-y-3">
          <h2 className="font-normal text-lg p-0 m-0">{t('COPT_STORAGE_TITLE')}</h2>
          <p className="m-0 text-grey-300 text-sm">{t('COPT_STORAGE_INSTRUCTIONS')}</p>
          <h3 className="mt-0 mb-3 text-base font-semibold">{t('COPT_STORAGE_FIELDS_TITLE')}</h3>
          {updatedData.volume_capacity !== undefined && (
            <NumberInput
              value={updatedData.volume_capacity}
              className={inputClass}
              required
              name="volume_capacity"
              label={t('COPT_STORAGE_VOLUME_CAPACITY')}
              onChange={(value: number) => {
                const volume_capacity = value != null && !Number.isNaN(value) ? value : null;
                setUpdatedData({
                  ...updatedData,
                  volume_capacity,
                });
                setChanges(changes.set('volume_capacity', volume_capacity));
              }}
              disabled={readOnly('volume_capacity')}
              rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
              endAdornment={<InfoTooltip field="volume_capacity" objectType="storage" />}
            />
          )}
          {updatedData.upper_soc_limit !== undefined && (
            <div className="flex gap-2 justify-between">
              {updatedData.upper_soc_limit !== undefined && updatedData.volume_capacity !== undefined && (
                <NumberInput
                  value={
                    updatedData.upper_soc_limit === null || updatedData.volume_capacity === null
                      ? updatedData.upper_soc_limit
                      : Math.round(updatedData.upper_soc_limit * updatedData.volume_capacity)
                  }
                  className={inputClass}
                  name="upper_soc_limit"
                  label={t('COPT_STORAGE_UPPER_SOC_LIMIT_CUBIC_METERS')}
                  required
                  onChange={(value: number) => {
                    const upper_soc_limit =
                      value != null && !Number.isNaN(value) ? Math.round(value) / updatedData.volume_capacity! : null;
                    setUpdatedData({
                      ...updatedData,
                      upper_soc_limit,
                    });
                    setChanges(changes.set('upper_soc_limit', upper_soc_limit));
                  }}
                  disabled={readOnly('upper_soc_limit')}
                  minValue={
                    (Number.isNaN(updatedData.lower_soc_limit) ? 0 : updatedData.lower_soc_limit!) *
                    updatedData.volume_capacity!
                  }
                  maxValue={updatedData.volume_capacity!}
                  rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                />
              )}
              {updatedData.upper_soc_limit !== undefined && (
                <NumberInput
                  value={updatedData.upper_soc_limit === null ? null : Math.round(updatedData.upper_soc_limit * 100)}
                  className={inputClass}
                  name="upper_soc_limit_percent"
                  label={t('COPT_STORAGE_LIMIT_PERCENT')}
                  onChange={(value: Nullable<number>) => {
                    const upper_soc_limit = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      upper_soc_limit,
                    });
                    setChanges(changes.set('upper_soc_limit', upper_soc_limit));
                  }}
                  disabled={readOnly('upper_soc_limit')}
                  minValue={Number.isNaN(updatedData.lower_soc_limit) ? 0 : updatedData.lower_soc_limit!}
                  maxValue={100}
                  rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                  endAdornment={<InfoTooltip field="upper_soc_limit" objectType="storage" />}
                />
              )}
            </div>
          )}
          {updatedData.lower_soc_limit !== undefined && (
            <div className="flex gap-2 justify-between">
              {updatedData.lower_soc_limit !== undefined && updatedData.volume_capacity !== undefined && (
                <NumberInput
                  value={
                    updatedData.lower_soc_limit === null || updatedData.volume_capacity === null
                      ? updatedData.lower_soc_limit
                      : Math.round(updatedData.lower_soc_limit * updatedData.volume_capacity)
                  }
                  className={inputClass}
                  name="lower_soc_limit"
                  label={t('COPT_STORAGE_LOWER_SOC_LIMIT_CUBIC_METERS')}
                  required
                  onChange={(value: number) => {
                    const lower_soc_limit =
                      value != null && !Number.isNaN(value) ? Math.round(value) / updatedData.volume_capacity! : null;
                    setUpdatedData({
                      ...updatedData,
                      lower_soc_limit,
                    });
                    setChanges(changes.set('lower_soc_limit', lower_soc_limit));
                  }}
                  disabled={readOnly('lower_soc_limit')}
                  notNegative
                  rules={
                    updatedData.upper_soc_limit != null &&
                    updatedData.upper_soc_limit > 0 &&
                    updatedData.volume_capacity != null
                      ? [
                          mustBeLessThan(
                            updatedData.upper_soc_limit * updatedData.volume_capacity,
                            tv('COMMON_MUST_BE_LESS_THAN', {
                              value: Math.round(updatedData.upper_soc_limit * updatedData.volume_capacity),
                            }),
                          ),
                        ]
                      : []
                  }
                />
              )}
              {updatedData.lower_soc_limit !== undefined && (
                <NumberInput
                  value={updatedData.lower_soc_limit === null ? null : Math.round(updatedData.lower_soc_limit * 100)}
                  className={inputClass}
                  name="lower_soc_limit_percent"
                  label={t('COPT_STORAGE_LIMIT_PERCENT')}
                  onChange={(value: Nullable<number>) => {
                    const lower_soc_limit = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      lower_soc_limit,
                    });
                    setChanges(changes.set('lower_soc_limit', lower_soc_limit));
                  }}
                  disabled={readOnly('lower_soc_limit')}
                  notNegative
                  rules={
                    updatedData.upper_soc_limit != null && updatedData.upper_soc_limit > 0
                      ? [
                          mustBeLessThan(
                            updatedData.upper_soc_limit * 100,
                            tv('COMMON_MUST_BE_LESS_THAN', { value: Math.round(updatedData.upper_soc_limit * 100) }),
                          ),
                        ]
                      : []
                  }
                  endAdornment={<InfoTooltip field="lower_soc_limit" objectType="storage" />}
                />
              )}
            </div>
          )}
          {updatedData.x_methane !== undefined && (
            <NumberInput
              value={updatedData.x_methane === null ? null : Math.round(updatedData.x_methane * 100)}
              className={inputClass}
              name="x_methane"
              label={t('COPT_STORAGE_X_METHANE')}
              required
              onChange={(value: number) => {
                const x_methane = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                setUpdatedData({
                  ...updatedData,
                  x_methane,
                });
                setChanges(changes.set('x_methane', x_methane));
              }}
              disabled={readOnly('x_methane')}
              minValue={40}
              maxValue={85}
              endAdornment={<InfoTooltip field="x_methane" objectType="storage" />}
            />
          )}
          {updatedData.fixed_storage_level_device_number !== undefined && (
            <NumberInput
              value={updatedData.fixed_storage_level_device_number ?? null}
              className={inputClass}
              name="fixed_storage_level_device_number"
              label={t('COPT_STORAGE_FIXED_STORAGE_LEVEL_DEVICE_NUMBER')}
              onChange={(value: Nullable<number>) => {
                const fixed_storage_level_device_number = value != null && !Number.isNaN(value) ? value : null;
                setUpdatedData({
                  ...updatedData,
                  fixed_storage_level_device_number,
                });
                setChanges(changes.set('fixed_storage_level_device_number', fixed_storage_level_device_number));
              }}
              disabled={readOnly('fixed_storage_level_device_number')}
              notNegative
              endAdornment={<InfoTooltip field="fixed_storage_level_device_number" objectType="storage" />}
            />
          )}
        </div>
        <div className="flex items-center bg-grey-100 border-solid border border-black/10 p-10">
          <GasStorageVisualization data={updatedData} />
        </div>
        {updatedData.soc_initialization_mode !== undefined && (
          <>
            <h3 className="mt-0 mb-3 text-base font-semibold xl:col-span-2">{t('COPT_STORAGE_FILL_LEVEL')}</h3>
            <FormControl
              className={clsx('w-full bg-white', {
                'xl:col-span-2': updatedData.soc_initialization_mode !== SoCInitializationMode.fixed_initial_level,
              })}
            >
              <InputLabel id="soc_initialization_mode">{t('COPT_STORAGE_SOC_INITIALIZATION_MODE')}</InputLabel>
              <Select
                id="soc_initialization_mode-select"
                value={updatedData.soc_initialization_mode}
                label={t('COPT_STORAGE_SOC_INITIALIZATION_MODE')}
                onChange={(e) => {
                  const soc_initialization_mode = e.target.value as SoCInitializationMode;
                  setUpdatedData({
                    ...updatedData,
                    soc_initialization_mode,
                  });
                  setChanges(changes.set('soc_initialization_mode', soc_initialization_mode));
                }}
                disabled={readOnly('soc_initialization_mode')}
                endAdornment={
                  <InfoTooltip
                    iconClass="pr-4 w-4 sm:text-green-200"
                    field="soc_initialization_mode"
                    objectType="storage"
                    title={t('COPT_STORAGE_SOC_INITIALIZATION_MODE')}
                    text={
                      <Trans components={{ p: <p />, ul: <ul />, li: <li />, strong: <strong /> }}>
                        COPT_STORAGE_SOC_INITIALIZATION_MODE_TOOLTIP
                      </Trans>
                    }
                  />
                }
              >
                {Object.entries(SoCInitializationMode).map((element) => (
                  <MenuItem key={element[0]} value={element[1]}>
                    {t(`COPT_STORAGE_SOC_INITIALIZATION_MODE_${element[1].toUpperCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {updatedData.soc_initialization_mode === SoCInitializationMode.manual_measurement &&
              updatedData.fill_level_entry?.default_value !== undefined && (
                <>
                  <NumberInput
                    value={
                      updatedData.fill_level_entry?.default_value?.fill_level == null
                        ? null
                        : Math.round(updatedData.fill_level_entry.default_value.fill_level * 100)
                    }
                    className={inputClass}
                    name="fill_level"
                    label={t('COPT_STORAGE_FILL_LEVEL_MANUAL_ENTRY')}
                    required
                    onChange={(value: number) => {
                      const fill_level_entry = {
                        enable_live_parameter: true,
                        default_value: {
                          fill_level: Math.round(value) / 100,
                          measured_at: updatedData.fill_level_entry?.default_value?.measured_at ?? null,
                        },
                      };
                      setUpdatedData({
                        ...updatedData,
                        fill_level_entry,
                      });
                      setChanges(changes.set('fill_level_entry', fill_level_entry));
                    }}
                    disabled={readOnly('fill_level')}
                    minValue={0}
                    maxValue={100}
                  />
                  <DateTimePicker
                    label={t('COPT_STORAGE_FILL_LEVEL_MANUAL_ENTRY_MEASURED_AT')}
                    mode="single"
                    required
                    pickerProps={{
                      disabled: {
                        after: new Date(),
                      },
                      numberOfMonths: 1,
                      fixedWeeks: true,
                      showWeekNumber: true,
                      showOutsideDays: true,
                      locale,
                      startOfWeek,
                    }}
                    timePickerProps={{
                      from: {
                        minutesStep: 1,
                      },
                    }}
                    textFieldProps={{
                      InputProps: {
                        endAdornment: (
                          <Button
                            type="button"
                            className="h-8 min-w-0 p-x-2"
                            onClick={(event) => {
                              event.stopPropagation();
                              const fill_level_entry = {
                                enable_live_parameter: true,
                                default_value: {
                                  fill_level: updatedData.fill_level_entry?.default_value?.fill_level ?? null,
                                  measured_at: new Date().toISOString(),
                                },
                              };
                              setUpdatedData({
                                ...updatedData,
                                fill_level_entry,
                              });
                              setChanges(changes.set('fill_level_entry', fill_level_entry));
                            }}
                          >
                            {t('SHARED_NOW')}
                          </Button>
                        ),
                      },
                    }}
                    value={
                      updatedData.fill_level_entry.default_value?.measured_at
                        ? parseISO(updatedData.fill_level_entry.default_value?.measured_at)
                        : undefined
                    }
                    onSelect={({ range }) => {
                      const fill_level_entry = {
                        enable_live_parameter: true,
                        default_value: {
                          fill_level: updatedData.fill_level_entry?.default_value?.fill_level ?? null,
                          measured_at: toDate(range.start).toISOString(),
                        },
                      };
                      setUpdatedData({
                        ...updatedData,
                        fill_level_entry,
                      });
                      setChanges(changes.set('fill_level_entry', fill_level_entry));
                    }}
                  />
                </>
              )}
            {updatedData.soc_initialization_mode === SoCInitializationMode.fixed_initial_level &&
              updatedData.fixed_initial_fill_level !== undefined && (
                <NumberInput
                  value={
                    updatedData.fixed_initial_fill_level === null
                      ? null
                      : Math.round(updatedData.fixed_initial_fill_level * 100)
                  }
                  className={inputClass}
                  name="fixed_initial_fill_level"
                  label={t('COPT_STORAGE_FIXED_INITIAL_FILL_LEVEL')}
                  required
                  onChange={(value: Nullable<number>) => {
                    const fixed_initial_fill_level =
                      value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                    setUpdatedData({
                      ...updatedData,
                      fixed_initial_fill_level,
                    });
                    setChanges(changes.set('fixed_initial_fill_level', fixed_initial_fill_level));
                  }}
                  disabled={readOnly('fixed_initial_fill_level')}
                  minValue={0}
                  maxValue={100}
                />
              )}
            {updatedData.soc_initialization_mode === SoCInitializationMode.fixed_weekly_fill_level && (
              <>
                <div className="w-full xl:col-span-2">
                  <NumberInput
                    value={
                      updatedData.fixed_weekly_fill_level_config?.fill_level == null
                        ? null
                        : Math.round(updatedData.fixed_weekly_fill_level_config?.fill_level * 100)
                    }
                    className={inputClass}
                    name="fixed_weekly_fill_level_config.fill_level"
                    label={t('COPT_STORAGE_FIXED_WEEKLY_FILL_LEVEL_CONFIG_FILL_LEVEL')}
                    onChange={(value: Nullable<number>) => {
                      const currentQuarterHour = roundToNearestMinutes(new Date(), {
                        roundingMethod: 'floor',
                        nearestTo: 15,
                      });
                      const fixed_weekly_fill_level_config = {
                        fill_level: value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null,
                        time:
                          updatedData.fixed_weekly_fill_level_config?.time ??
                          `${getHours(currentQuarterHour)}:${getMinutes(currentQuarterHour)}`,
                        weekday: updatedData.fixed_weekly_fill_level_config?.weekday ?? getDay(new Date()) - 1,
                      } as WeeklyFillLevelConfig;
                      setUpdatedData({
                        ...updatedData,
                        fixed_weekly_fill_level_config,
                      });
                      setChanges(changes.set('fixed_weekly_fill_level_config', fixed_weekly_fill_level_config));
                    }}
                    disabled={readOnly('fixed_weekly_fill_level_config')}
                    notNegative
                    required
                    rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
                    endAdornment={
                      <InfoTooltip
                        title={t('COPT_STORAGE_FIXED_WEEKLY_FILL_LEVEL_CONFIG_FILL_LEVEL')}
                        text={t('COPT_STORAGE_FIXED_WEEKLY_FILL_LEVEL_CONFIG_FILL_LEVEL_TOOLTIP')}
                        field="fixed_weekly_fill_level_config.fill_level"
                        objectType="storage"
                      />
                    }
                  />
                </div>
                <div className="xl:col-span-2 rounded-md border-solid border border-divider p-2">
                  <div className="text-sm xl:col-span-2 pb-2">
                    {t('COPT_STORAGE_FIXED_WEEKLY_FILL_LEVEL_CONFIG_TIME_WEEKDAY_TITLE')}
                    <InfoTooltip
                      iconClass="w-4 sm:text-green-200 -mb-2"
                      text={t('COPT_STORAGE_FIXED_WEEKLY_FILL_LEVEL_CONFIG_TIME_TOOLTIP')}
                      field="fixed_weekly_fill_level_config.time fixed_weekly_fill_level_config.weekday"
                    />
                  </div>

                  <div className="flex flex-col md:flex-row">
                    <div className="mt-4 md:mt-0 md:w-1/2">
                      <TimePicker
                        value={
                          updatedData.fixed_weekly_fill_level_config?.time
                            ? new Date().setHours(
                                getTime(updatedData.fixed_weekly_fill_level_config.time)[0],
                                getTime(updatedData.fixed_weekly_fill_level_config.time)[1],
                              )
                            : new Date()
                        }
                        disabled={readOnly('fixed_weekly_fill_level_config')}
                        hoursStep={1}
                        minutesStep={15}
                        onChange={({ value }) => {
                          const hours = getHours(value).toString().padStart(2, '0');
                          const minutes = getMinutes(value).toString().padStart(2, '0');
                          const fixed_weekly_fill_level_config = {
                            ...updatedData.fixed_weekly_fill_level_config,
                            time: `${hours}:${minutes}`,
                          } as WeeklyFillLevelConfig;
                          setUpdatedData({
                            ...updatedData,
                            fixed_weekly_fill_level_config,
                          });
                          setChanges(changes.set('fixed_weekly_fill_level_config', fixed_weekly_fill_level_config));
                        }}
                      />
                    </div>

                    <WeekdaySelector
                      pythonMode
                      selected={
                        updatedData.fixed_weekly_fill_level_config?.weekday !== undefined
                          ? ([updatedData.fixed_weekly_fill_level_config?.weekday].reduce(
                              (a, v) => ({ ...a, [v]: true }),
                              {},
                            ) as WeekdaySelection)
                          : undefined
                      }
                      disabled={readOnly('fixed_weekly_fill_level_config')}
                      className={clsx(
                        'md:w-1/2 border-0 mt-4 md:mt-0 md:ml-4 rounded-md bg-white',

                        {
                          'opacity-70': readOnly('fixed_weekly_fill_level_config'),
                        },
                      )}
                      onChange={(selected) => {
                        const weekday = Object.entries(selected).find(([day, value]) => value === true);
                        const fixed_weekly_fill_level_config = {
                          ...updatedData.fixed_weekly_fill_level_config,
                          weekday: weekday?.[0],
                        } as unknown as WeeklyFillLevelConfig;

                        setUpdatedData({
                          ...updatedData,
                          fixed_weekly_fill_level_config,
                        });
                        setChanges(changes.set('fixed_weekly_fill_level_config', fixed_weekly_fill_level_config));
                      }}
                      oneDayMode
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
        <div className="xl:col-span-2">
          <NumberInput
            value={updatedData.end_level_tolerance == null ? null : Math.round(updatedData.end_level_tolerance * 100)}
            className={inputClass}
            name="end_level_tolerance"
            label={t('COPT_STORAGE_END_LEVEL_TOLERANCE')}
            onChange={(value: Nullable<number>) => {
              const end_level_tolerance = value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
              setUpdatedData({
                ...updatedData,
                end_level_tolerance,
              });
              setChanges(changes.set('end_level_tolerance', end_level_tolerance));
            }}
            disabled={readOnly('end_level_tolerance')}
            minValue={0}
            maxValue={100}
            endAdornment={
              <InfoTooltip
                text={
                  <Trans components={{ p: <p />, code: <code className="text-center font-semibold text-xs" /> }}>
                    {t('COPT_STORAGE_END_LEVEL_TOLERANCE_TOOLTIP', {
                      end_level_tolerance: t('COPT_STORAGE_END_LEVEL_TOLERANCE'),
                      target_power_electric: t('COPT_GAS_PRODUCTION_TARGET_POWER_ELECTRIC'),
                      conversion_efficiency: t('COPT_GAS_PRODUCTION_TARGET_POWER_ELECTRIC'),
                      lower_soc_limit: t('COPT_STORAGE_LOWER_SOC_LIMIT'),
                      upper_soc_limit: t('COPT_STORAGE_UPPER_SOC_LIMIT'),
                    })}
                  </Trans>
                }
                field="end_level_tolerance"
                objectType="storage"
              />
            }
          />
        </div>
        {updatedData.short_term_soft_band !== undefined && (
          <NumberInput
            value={Math.round(updatedData.short_term_soft_band * 100)}
            className={inputClass}
            name="short_term_soft_band"
            label={t('COPT_STORAGE_SHORT_TERM_SOFT_BAND')}
            required
            onChange={(value: number) => {
              const short_term_soft_band = Math.round(value) / 100;
              setUpdatedData({
                ...updatedData,
                short_term_soft_band,
              });
              setChanges(changes.set('short_term_soft_band', short_term_soft_band));
            }}
            disabled={readOnly('short_term_soft_band')}
            minValue={0}
            maxValue={100}
            endAdornment={
              <InfoTooltip
                title={t('COPT_STORAGE_SHORT_TERM_SOFT_BAND')}
                text={t('COPT_STORAGE_SHORT_TERM_SOFT_BAND_TOOLTIP', {
                  short_term_soft_band_price: t('COPT_STORAGE_SHORT_TERM_SOFT_BAND_PRICE'),
                })}
                field="short_term_soft_band"
                objectType="storage"
              />
            }
          />
        )}
        {updatedData.short_term_soft_band_price !== undefined && (
          <NumberInput
            value={updatedData.short_term_soft_band_price}
            className={inputClass}
            name="short_term_soft_band_price"
            label={t('COPT_STORAGE_SHORT_TERM_SOFT_BAND_PRICE')}
            onChange={(value: number) => {
              setUpdatedData({
                ...updatedData,
                short_term_soft_band_price: value,
              });
              setChanges(changes.set('short_term_soft_band_price', value));
            }}
            disabled={readOnly('short_term_soft_band_price')}
            notNegative
            rules={[mustBeLessThan(10000, tv('COMMON_MUST_BE_LESS_THAN', { value: 10000 }))]}
            required
            endAdornment={<InfoTooltip field="short_term_soft_band_price" objectType="storage" />}
          />
        )}
        {(updatedData.breach_reaction_storage_reduction !== undefined ||
          updatedData.breach_reaction_time !== undefined ||
          updatedData.open_afrr_storage_reduction_time_lower !== undefined ||
          updatedData.open_afrr_storage_reduction_time_upper !== undefined ||
          updatedData.closed_afrr_storage_reduction_time_lower !== undefined ||
          updatedData.closed_afrr_storage_reduction_time_upper !== undefined ||
          updatedData.day_ahead_storage_reduction_time_lower !== undefined ||
          updatedData.day_ahead_storage_reduction_time_upper !== undefined) && (
          <h3 className="mt-0 mb-3 text-base font-semibold xl:col-span-2">{t('COPT_STORAGE_BREACH_CONFIG')}</h3>
        )}
        {updatedData.breach_reaction_storage_reduction !== undefined && (
          <NumberInput
            value={
              updatedData.breach_reaction_storage_reduction === null
                ? null
                : Math.round(updatedData.breach_reaction_storage_reduction * 10000) / 100
            }
            className={inputClass}
            name="breach_reaction_storage_reduction"
            label={t('COPT_STORAGE_BREACH_REACTION_STORAGE_REDUCTION')}
            onChange={(value: Nullable<number>) => {
              const breach_reaction_storage_reduction =
                value != null && !Number.isNaN(value) ? Math.round(value * 100) / 10000 : null;
              setUpdatedData({
                ...updatedData,
                breach_reaction_storage_reduction,
              });
              setChanges(changes.set('breach_reaction_storage_reduction', breach_reaction_storage_reduction));
            }}
            disabled={readOnly('breach_reaction_storage_reduction')}
            notNegative
            rules={[mustBeLessThan(100, tv('COMMON_MUST_BE_LESS_THAN', { value: 100 }))]}
            endAdornment={<InfoTooltip field="breach_reaction_storage_reduction" objectType="storage" />}
          />
        )}
        {updatedData.breach_reaction_time !== undefined && (
          <NumberInput
            value={updatedData.breach_reaction_time ?? null}
            className={inputClass}
            name="breach_reaction_time"
            label={t('COPT_STORAGE_BREACH_REACTION_TIME')}
            onChange={(value: Nullable<number>) => {
              const breach_reaction_time = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                breach_reaction_time,
              });
              setChanges(changes.set('breach_reaction_time', breach_reaction_time));
            }}
            disabled={readOnly('breach_reaction_time')}
            notNegative
            endAdornment={<InfoTooltip field="breach_reaction_time" objectType="storage" />}
          />
        )}
        {(updatedData.open_afrr_storage_reduction_time_lower !== undefined ||
          updatedData.open_afrr_storage_reduction_time_upper !== undefined) && (
          <h4 className="mt-0 mb-3 text-sm font-semibold xl:col-span-2">
            {t('COPT_STORAGE_OPEN_AFRR_STORAGE_REDUCTION')}
          </h4>
        )}
        {updatedData.open_afrr_storage_reduction_time_lower !== undefined && (
          <NumberInput
            value={updatedData.open_afrr_storage_reduction_time_lower ?? null}
            className={inputClass}
            name="open_afrr_storage_reduction_time_lower"
            label={t('COPT_STORAGE_OPEN_AFRR_STORAGE_REDUCTION_TIME_LOWER')}
            required
            onChange={(value: number) => {
              const open_afrr_storage_reduction_time_lower = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                open_afrr_storage_reduction_time_lower,
              });
              setChanges(changes.set('open_afrr_storage_reduction_time_lower', open_afrr_storage_reduction_time_lower));
            }}
            disabled={readOnly('open_afrr_storage_reduction_time_lower')}
            notNegative
            endAdornment={<InfoTooltip field="open_afrr_storage_reduction_time_lower" objectType="storage" />}
          />
        )}
        {updatedData.open_afrr_storage_reduction_time_upper !== undefined && (
          <NumberInput
            value={updatedData.open_afrr_storage_reduction_time_upper ?? null}
            className={inputClass}
            name="open_afrr_storage_reduction_time_upper"
            label={t('COPT_STORAGE_OPEN_AFRR_STORAGE_REDUCTION_TIME_UPPER')}
            required
            onChange={(value: number) => {
              const open_afrr_storage_reduction_time_upper = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                open_afrr_storage_reduction_time_upper,
              });
              setChanges(changes.set('open_afrr_storage_reduction_time_upper', open_afrr_storage_reduction_time_upper));
            }}
            disabled={readOnly('open_afrr_storage_reduction_time_upper')}
            notNegative
            endAdornment={<InfoTooltip field="open_afrr_storage_reduction_time_upper" objectType="storage" />}
          />
        )}
        {(updatedData.closed_afrr_storage_reduction_time_lower !== undefined ||
          updatedData.closed_afrr_storage_reduction_time_upper !== undefined) && (
          <h4 className="mt-0 mb-3 text-sm font-semibold xl:col-span-2">
            {t('COPT_STORAGE_CLOSED_AFRR_STORAGE_REDUCTION')}
          </h4>
        )}
        {updatedData.closed_afrr_storage_reduction_time_lower !== undefined && (
          <NumberInput
            value={updatedData.closed_afrr_storage_reduction_time_lower ?? null}
            className={inputClass}
            name="closed_afrr_storage_reduction_time_lower"
            label={t('COPT_STORAGE_CLOSED_AFRR_STORAGE_REDUCTION_TIME_LOWER')}
            required
            onChange={(value: number) => {
              const closed_afrr_storage_reduction_time_lower = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                closed_afrr_storage_reduction_time_lower,
              });
              setChanges(
                changes.set('closed_afrr_storage_reduction_time_lower', closed_afrr_storage_reduction_time_lower),
              );
            }}
            disabled={readOnly('closed_afrr_storage_reduction_time_lower')}
            notNegative
            endAdornment={<InfoTooltip field="closed_afrr_storage_reduction_time_lower" objectType="storage" />}
          />
        )}
        {updatedData.closed_afrr_storage_reduction_time_upper !== undefined && (
          <NumberInput
            value={updatedData.closed_afrr_storage_reduction_time_upper ?? null}
            className={inputClass}
            name="closed_afrr_storage_reduction_time_upper"
            label={t('COPT_STORAGE_CLOSED_AFRR_STORAGE_REDUCTION_TIME_UPPER')}
            required
            onChange={(value: number) => {
              const closed_afrr_storage_reduction_time_upper = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                closed_afrr_storage_reduction_time_upper,
              });
              setChanges(
                changes.set('closed_afrr_storage_reduction_time_upper', closed_afrr_storage_reduction_time_upper),
              );
            }}
            disabled={readOnly('closed_afrr_storage_reduction_time_upper')}
            notNegative
            endAdornment={<InfoTooltip field="closed_afrr_storage_reduction_time_upper" objectType="storage" />}
          />
        )}
        {(updatedData.day_ahead_storage_reduction_time_lower !== undefined ||
          updatedData.day_ahead_storage_reduction_time_upper !== undefined) && (
          <h4 className="mt-0 mb-3 text-sm font-semibold xl:col-span-2">
            {t('COPT_STORAGE_DAY_AHEAD_STORAGE_REDUCTION')}
          </h4>
        )}
        {updatedData.day_ahead_storage_reduction_time_lower !== undefined && (
          <NumberInput
            value={updatedData.day_ahead_storage_reduction_time_lower ?? null}
            className={inputClass}
            name="day_ahead_storage_reduction_time_lower"
            label={t('COPT_STORAGE_DAY_AHEAD_STORAGE_REDUCTION_TIME_LOWER')}
            required
            onChange={(value: number) => {
              const day_ahead_storage_reduction_time_lower = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                day_ahead_storage_reduction_time_lower,
              });
              setChanges(changes.set('day_ahead_storage_reduction_time_lower', day_ahead_storage_reduction_time_lower));
            }}
            disabled={readOnly('day_ahead_storage_reduction_time_lower')}
            notNegative
            endAdornment={<InfoTooltip field="day_ahead_storage_reduction_time_lower" objectType="storage" />}
          />
        )}
        {updatedData.day_ahead_storage_reduction_time_upper !== undefined && (
          <NumberInput
            value={updatedData.day_ahead_storage_reduction_time_upper ?? null}
            className={inputClass}
            name="day_ahead_storage_reduction_time_upper"
            label={t('COPT_STORAGE_DAY_AHEAD_STORAGE_REDUCTION_TIME_UPPER')}
            required
            onChange={(value: number) => {
              const day_ahead_storage_reduction_time_upper = value != null && !Number.isNaN(value) ? value : null;
              setUpdatedData({
                ...updatedData,
                day_ahead_storage_reduction_time_upper,
              });
              setChanges(changes.set('day_ahead_storage_reduction_time_upper', day_ahead_storage_reduction_time_upper));
            }}
            disabled={readOnly('day_ahead_storage_reduction_time_upper')}
            notNegative
            endAdornment={<InfoTooltip field="day_ahead_storage_reduction_time_upper" objectType="storage" />}
          />
        )}
      </ValidatedContainer>
      {!readOnly() && (
        <div className="flex gap-2 justify-between xl:col-span-2">
          <Button color="primary" variant="outlined" onClick={resetValues}>
            {t('SHARED_CANCEL')}
          </Button>
          <Button
            color="primary"
            disabled={!isValid || isSaving}
            onClick={() => updateGasStorageValues(isValid)}
            startIcon={isSaving && <CircularProgress size={16} />}
          >
            {t('SHARED_SAVE')}
          </Button>
        </div>
      )}
    </div>
  );
}
